/**
 * Where OS sits (web-redesign-08-os.md, Decided 4 and 5; os-live-mode.md 4.3
 * and 4.7), as pure rules, so the panel, the pill and the Layout button only
 * draw them.
 *
 * - **The layout** is the person's choice: floating over the page, the side
 *   sheet beside it, or the full OS page. It is kept per person, per browser,
 *   and OS opens there next time.
 * - **The side sheet needs a place.** On a page with none of its own (the
 *   records' home, search, Account), OS left in the side sheet floats.
 * - **The pill's corner.** Only the collapsed pill moves, to one of the four
 *   corners of the page's box, 24 in: dragged there, or moved with the arrow
 *   keys. The open panel always sits at the bottom right, and collapsing puts
 *   the pill back there.
 */

export type OsLayout = 'float' | 'side' | 'page';

export const OS_LAYOUTS: readonly OsLayout[] = ['float', 'side', 'page'];

/**
 * The layout the shortcut (⇧⌘L) moves to: the next in Floating · Side sheet ·
 * Full page, round again, past the side sheet where the page has no place for
 * it.
 */
export const nextLayoutOf = (current: OsLayout, sideAvailable: boolean): OsLayout => {
  const open = OS_LAYOUTS.filter((one) => one !== 'side' || sideAvailable || one === current);
  return open[(open.indexOf(current) + 1) % open.length] ?? current;
};

/**
 * Where the Ask OS orb, or ⌘J with OS closed, opens OS (12 Oct, Devendra): over
 * the page or beside it, never the full page, whatever layout was left last,
 * as a jump to another page felt like a bug the person had to go back from.
 * Full page left last opens floating, and stays the layout from then on.
 */
export const orbLayoutOf = (kept: OsLayout): Exclude<OsLayout, 'page'> => (kept === 'page' ? 'float' : kept);

/** Whether a key press is the layout shortcut: ⇧⌘L (⇧ Ctrl L off a Mac). */
export const isLayoutKey = (event: { readonly key: string; readonly metaKey: boolean; readonly ctrlKey: boolean; readonly shiftKey: boolean; readonly altKey: boolean }): boolean =>
  event.key.toLowerCase() === 'l' && (event.metaKey || event.ctrlKey) && event.shiftKey && !event.altKey;

export type PillCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export const HOME_CORNER: PillCorner = 'bottom-right';

/** What the browser keeps for one person. */
export type OsWhere = { readonly layout: OsLayout; readonly corner: PillCorner };

/** OS the first time: floating, for anything quick on any page (the board "Three places OS sits"). */
export const FIRST_WHERE: OsWhere = { layout: 'float', corner: HOME_CORNER };

const CORNERS: readonly PillCorner[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];

const isLayout = (raw: unknown): raw is OsLayout => typeof raw === 'string' && (OS_LAYOUTS as readonly string[]).includes(raw);
const isCorner = (raw: unknown): raw is PillCorner => typeof raw === 'string' && (CORNERS as readonly string[]).includes(raw);

/** Every person's choice, as the browser keeps it: `{ [userId]: OsWhere }`. */
export type WhereByPerson = Readonly<Record<string, OsWhere>>;

/** The kept choices read back; anything this code did not write is dropped. */
export const readWhereByPerson = (raw: string | null): WhereByPerson => {
  if (raw === null) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const kept: Record<string, OsWhere> = {};
    for (const [userId, one] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof one !== 'object' || one === null) continue;
      const { layout, corner } = one as { layout?: unknown; corner?: unknown };
      if (isLayout(layout)) kept[userId] = { layout, corner: isCorner(corner) ? corner : HOME_CORNER };
    }
    return kept;
  } catch {
    return {};
  }
};

/** Where OS sat for this person, or where it first sits. */
export const whereFor = (all: WhereByPerson, userId: string | null): OsWhere =>
  userId === null ? FIRST_WHERE : (all[userId] ?? FIRST_WHERE);

/** How OS is drawn over this page when open: the full page is a route of its own, so here it is floating or beside. */
export const shownAs = (layout: OsLayout, sidePlace: boolean): 'float' | 'side' => (layout === 'side' && sidePlace ? 'side' : 'float');

/** The corner an arrow key moves the pill to: left and right change the side, up and down the edge. */
export const cornerAfterKey = (corner: PillCorner, key: string): PillCorner | null => {
  const [edge, side] = corner.split('-') as ['top' | 'bottom', 'left' | 'right'];
  if (key === 'ArrowLeft') return `${edge}-left`;
  if (key === 'ArrowRight') return `${edge}-right`;
  if (key === 'ArrowUp') return `top-${side}`;
  if (key === 'ArrowDown') return `bottom-${side}`;
  return null;
};

/** The corner a pill let go at this point lands in: the quarter of the box the point is in. */
export const cornerAt = (point: { x: number; y: number }, box: { left: number; top: number; width: number; height: number }): PillCorner => {
  const edge = point.y < box.top + box.height / 2 ? 'top' : 'bottom';
  const side = point.x < box.left + box.width / 2 ? 'left' : 'right';
  return `${edge}-${side}`;
};

/**
 * What stands for OS while it is minimised (C13, J26, J33): with live mode
 * running, the minimised live pill in the Ask OS orb's spot (8 in, growing
 * leftward), which never moves; with it off, today's pill, which the person
 * drags to any corner.
 */
export type MinimisedPill = 'none' | 'live' | 'drag';

export const minimisedPillOf = ({ minimised, live }: { readonly minimised: boolean; readonly live: boolean }): MinimisedPill =>
  !minimised ? 'none' : live ? 'live' : 'drag';

/**
 * Live mode is the panel's or the side sheet's only while the call is on the
 * thread it draws. A call another surface holds (the Dashboard door, the OS
 * page on another thread) leaves the panel as it is: its pill, head, Esc and
 * Close read chat mode.
 */
export const liveHereOf = ({ live, callThreadId, threadId }: { readonly live: boolean; readonly callThreadId: string | null; readonly threadId: string | null }): boolean =>
  live && threadId !== null && callThreadId === threadId;

/** Only the pill of a minimised OS with live mode off is dragged; the live pill keeps the orb's spot. */
export const pillDraggable = (pill: MinimisedPill): boolean => pill === 'drag';

/** How far in the live pill sits from the page box's right and foot: the Ask OS orb's own spot. */
export const LIVE_PILL_INSET = 8;

/** A card that needs a click while OS is minimised in live mode opens the panel by itself, its Save ringed. */
export const opensBySelf = (state: string): boolean => state === 'needs_tap';

/**
 * Only entering that wait opens OS. The pill mounts with the state that
 * already holds (`was` is it then), so Minimize or Esc while a card waits
 * stays minimised; a wait that begins while minimised opens OS.
 */
export const opensBySelfOn = ({ was, now }: { readonly was: string | null; readonly now: string | null }): boolean =>
  now !== null && opensBySelf(now) && (was === null || !opensBySelf(was));
